/**
 * groupReordering.ts - Utilities for reordering node groups in the automation flow
 *
 * Provides functions for:
 * - Moving groups to different positions in the flow
 * - Rewiring edges when groups are moved
 * - Reconnecting orphaned nodes after partial moves
 *
 * Related:
 *   - hooks/useSelectionGroup.ts - Builds SelectionGroup
 *   - hooks/useGroupDrag.ts - Drag operations
 *   - utils/nodeReordering.ts - Single node operations
 */

import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import type { SelectionGroup, MoveGroupResult } from '../types/selectionGroup';
import { applyLayout } from './layoutEngine';
import { getChildNodeIds, getParentNodeId } from './graphHelpers';

/**
 * Move a selection group to be after a target node in the flow.
 *
 * This operation:
 * 1. Removes external edges (incoming and outgoing from the group)
 * 2. Creates a new edge from target to group entry node
 * 3. Reconnects the group's old parent to the group's old successor (orphan reconnection)
 * 4. Connects group exits to target's former child (if any)
 * 5. Re-applies layout
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param group - The SelectionGroup to move
 * @param targetNodeId - ID of node to place the group after
 * @param sourceHandle - Optional source handle for switch node branches
 */
export function moveGroupAfter(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	group: SelectionGroup,
	targetNodeId: string,
	sourceHandle?: string
): MoveGroupResult {
	const targetNode = nodes.find(n => n.id === targetNodeId);

	if (!targetNode) {
		return {
			nodes,
			edges,
			success: false,
			message: 'Target node not found',
		};
	}

	// Can't move to a node within the group
	if (group.nodeIds.includes(targetNodeId)) {
		return {
			nodes,
			edges,
			success: false,
			message: 'Cannot move to a node within the selection',
		};
	}

	// Step 1: Find the group's old parent (before any edge removal)
	const oldParentId = getGroupOldParent(group, edges);
	const oldSuccessorId = getGroupOldSuccessor(group, edges);

	// Step 2: Remove external edges (incoming and outgoing from the group)
	let newEdges = edges.filter(e => {
		// Keep internal edges
		if (group.internalEdgeIds.includes(e.id)) {
			return true;
		}
		// Remove incoming edges to the group
		if (group.incomingEdgeIds.includes(e.id)) {
			return false;
		}
		// Remove outgoing edges from the group
		if (group.outgoingEdgeIds.includes(e.id)) {
			return false;
		}
		// Keep all other edges
		return true;
	});

	// Step 3: Find and remove target's child edge (if it would conflict)
	// Only remove the edge from target if we're inserting into the same branch
	const targetChildren = getChildNodeIds(targetNodeId, newEdges);
	const targetChildId = targetChildren.find(childId => {
		// If sourceHandle is specified, only remove the child from that handle
		if (sourceHandle) {
			const edge = newEdges.find(
				e => e.source === targetNodeId && e.target === childId && e.sourceHandle === sourceHandle
			);
			return !!edge;
		}
		// Otherwise, remove the first child
		return true;
	});

	if (targetChildId) {
		newEdges = newEdges.filter(e => {
			if (e.source === targetNodeId && e.target === targetChildId) {
				if (sourceHandle) {
					return e.sourceHandle !== sourceHandle;
				}
				return false;
			}
			return true;
		});
	}

	// Step 4: Create new edge from target to group entry
	newEdges.push({
		id: `edge-${targetNodeId}-${group.entryNodeId}-${Date.now()}`,
		source: targetNodeId,
		target: group.entryNodeId,
		sourceHandle,
		type: sourceHandle ? 'branch' : 'default',
	});

	// Step 5: Connect group exits to target's former child (if exists and group has single exit)
	if (targetChildId && group.exitNodeIds.length === 1) {
		newEdges.push({
			id: `edge-${group.exitNodeIds[0]}-${targetChildId}-${Date.now()}`,
			source: group.exitNodeIds[0],
			target: targetChildId,
			type: 'default',
		});
	}

	// Step 6: Reconnect the group's old parent to the group's old successor (orphan reconnection)
	// This handles the "partial selection" case where we leave descendants behind
	if (oldParentId && oldSuccessorId && !group.nodeIds.includes(oldSuccessorId)) {
		// Check if parent now has no children in that path
		const parentChildren = getChildNodeIds(oldParentId, newEdges);
		if (parentChildren.length === 0 || !parentChildren.some(id => !group.nodeIds.includes(id))) {
			newEdges.push({
				id: `edge-${oldParentId}-${oldSuccessorId}-${Date.now()}`,
				source: oldParentId,
				target: oldSuccessorId,
				type: 'default',
			});
		}
	}

	// Step 7: Re-apply layout
	const layoutResult = applyLayout(nodes, newEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		success: true,
	};
}

/**
 * Move a selection group to the end of a specific switch branch.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param group - The SelectionGroup to move
 * @param switchNodeId - ID of the switch node
 * @param caseIndex - Index of the case/branch to move to
 */
export function moveGroupToBranch(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	group: SelectionGroup,
	switchNodeId: string,
	caseIndex: number
): MoveGroupResult {
	const switchNode = nodes.find(n => n.id === switchNodeId);

	if (!switchNode) {
		return {
			nodes,
			edges,
			success: false,
			message: 'Switch node not found',
		};
	}

	// Find the last node in the specified branch
	const branchEdges = edges.filter(e => e.source === switchNodeId && e.data?.caseIndex === caseIndex);

	if (branchEdges.length === 0) {
		// Branch is empty, connect directly to switch
		return moveGroupAfter(nodes, edges, group, switchNodeId, `case-${caseIndex}`);
	}

	// Find the terminus of this branch
	const firstBranchNodeId = branchEdges[0].target;
	const branchTerminus = findBranchTerminus(firstBranchNodeId, edges);

	// Move group after the branch terminus
	return moveGroupAfter(nodes, edges, group, branchTerminus);
}

/**
 * Move a selection group to be before a target node in the flow.
 *
 * This inserts the group between the target's parent and the target.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param group - The SelectionGroup to move
 * @param targetNodeId - ID of node to place the group before
 */
export function moveGroupBefore(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	group: SelectionGroup,
	targetNodeId: string
): MoveGroupResult {
	const targetNode = nodes.find(n => n.id === targetNodeId);

	if (!targetNode) {
		return {
			nodes,
			edges,
			success: false,
			message: 'Target node not found',
		};
	}

	// Can't move before trigger
	if (targetNode.type === 'trigger') {
		return {
			nodes,
			edges,
			success: false,
			message: 'Cannot move before trigger node',
		};
	}

	// Can't move to a node within the group
	if (group.nodeIds.includes(targetNodeId)) {
		return {
			nodes,
			edges,
			success: false,
			message: 'Cannot move to a node within the selection',
		};
	}

	// Get target's parent
	const targetParentId = getParentNodeId(targetNodeId, edges);
	if (!targetParentId) {
		return {
			nodes,
			edges,
			success: false,
			message: 'Target has no parent',
		};
	}

	// Find the edge from parent to target to get sourceHandle
	const parentToTargetEdge = edges.find(e => e.source === targetParentId && e.target === targetNodeId);
	const sourceHandle = parentToTargetEdge?.sourceHandle;

	// Step 1: Find the group's old parent and successor for orphan reconnection
	const oldParentId = getGroupOldParent(group, edges);
	const oldSuccessorId = getGroupOldSuccessor(group, edges);

	// Step 2: Remove external edges
	const newEdges = edges.filter(e => {
		// Keep internal edges
		if (group.internalEdgeIds.includes(e.id)) {
			return true;
		}
		// Remove incoming edges to the group
		if (group.incomingEdgeIds.includes(e.id)) {
			return false;
		}
		// Remove outgoing edges from the group
		if (group.outgoingEdgeIds.includes(e.id)) {
			return false;
		}
		// Remove edge from target's parent to target
		if (e.source === targetParentId && e.target === targetNodeId) {
			return false;
		}
		return true;
	});

	// Step 3: Create edge from target's parent to group entry
	newEdges.push({
		id: `edge-${targetParentId}-${group.entryNodeId}-${Date.now()}`,
		source: targetParentId,
		target: group.entryNodeId,
		sourceHandle,
		type: sourceHandle ? 'branch' : 'default',
	});

	// Step 4: Connect group exit to target (assuming single exit for simplicity)
	if (group.exitNodeIds.length === 1) {
		newEdges.push({
			id: `edge-${group.exitNodeIds[0]}-${targetNodeId}-${Date.now()}`,
			source: group.exitNodeIds[0],
			target: targetNodeId,
			type: 'default',
		});
	}

	// Step 5: Reconnect orphaned nodes
	if (oldParentId && oldSuccessorId && !group.nodeIds.includes(oldSuccessorId)) {
		const parentChildren = getChildNodeIds(oldParentId, newEdges);
		if (parentChildren.length === 0 || !parentChildren.some(id => !group.nodeIds.includes(id))) {
			newEdges.push({
				id: `edge-${oldParentId}-${oldSuccessorId}-${Date.now()}`,
				source: oldParentId,
				target: oldSuccessorId,
				type: 'default',
			});
		}
	}

	// Step 6: Re-apply layout
	const layoutResult = applyLayout(nodes, newEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		success: true,
	};
}

/**
 * Get the group's old parent (the node that had an edge into the group entry)
 */
function getGroupOldParent(group: SelectionGroup, edges: Edge[]): string | null {
	// The incoming edge's source is the old parent
	for (const edgeId of group.incomingEdgeIds) {
		const edge = edges.find(e => e.id === edgeId);
		if (edge) {
			return edge.source;
		}
	}
	return null;
}

/**
 * Get the group's old successor (the node that was after the group's exit)
 */
function getGroupOldSuccessor(group: SelectionGroup, edges: Edge[]): string | null {
	// For partial moves, find what was connected to the exit node(s)
	for (const edgeId of group.outgoingEdgeIds) {
		const edge = edges.find(e => e.id === edgeId);
		if (edge) {
			return edge.target;
		}
	}
	return null;
}

/**
 * Find the terminal node in a branch (node with no children)
 */
function findBranchTerminus(startNodeId: string, edges: Edge[]): string {
	let currentId = startNodeId;
	const visited = new Set<string>();
	const maxIterations = 1000;

	for (let i = 0; i < maxIterations; i++) {
		if (visited.has(currentId)) {
			// Cycle detected, return current
			return currentId;
		}
		visited.add(currentId);

		const children = getChildNodeIds(currentId, edges);
		if (children.length === 0) {
			return currentId;
		}
		// Follow the first child (for linear chains)
		currentId = children[0];
	}

	// Fallback: return current node if max iterations reached
	return currentId;
}
